import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Sparkles,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Link as LinkIcon,
  Users,
  Mail,
  Building2,
  Calendar,
  DollarSign,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { LeadEnrichmentService } from '@/services/lead-enrichment.service';
import { Button, Card, Badge } from '@/components/common';

export interface EnrichmentPanelProps {
  leadId: string;
}

export const EnrichmentPanel: React.FC<EnrichmentPanelProps> = ({ leadId }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const { data: enrichmentStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['lead-enrichment-status', leadId],
    queryFn: () => LeadEnrichmentService.getEnrichmentStatus(leadId),
  });

  const { data: enrichedData } = useQuery({
    queryKey: ['lead-enriched-data', leadId],
    queryFn: () => LeadEnrichmentService.getEnrichedData(leadId),
    enabled: enrichmentStatus?.status === 'enriched',
  });

  const enrichMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id) throw new Error('Not authenticated');
      await LeadEnrichmentService.enrichLead(leadId, profile.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-enrichment-status', leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead-enriched-data', leadId] });
      queryClient.invalidateQueries({ queryKey: ['pilot-crm-lead', leadId] });
    },
  });

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'enriched':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500 animate-pulse" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Sparkles className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'enriched':
        return <Badge variant="success" size="sm">Enriched</Badge>;
      case 'pending':
        return <Badge variant="warning" size="sm">Pending</Badge>;
      case 'failed':
        return <Badge variant="danger" size="sm">Failed</Badge>;
      default:
        return <Badge variant="gray" size="sm">Not Enriched</Badge>;
    }
  };

  if (statusLoading) {
    return (
      <Card>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-20 bg-gray-100 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {getStatusIcon(enrichmentStatus?.status)}
          <h3 className="text-lg font-semibold text-gray-900">Data Enrichment</h3>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(enrichmentStatus?.status)}
          <Button
            variant="outline"
            size="sm"
            icon={RefreshCw}
            onClick={() => enrichMutation.mutate()}
            isLoading={enrichMutation.isPending || enrichmentStatus?.status === 'pending'}
            disabled={enrichmentStatus?.status === 'pending'}
          >
            {enrichmentStatus?.status === 'enriched' ? 'Re-enrich' : 'Enrich'}
          </Button>
        </div>
      </div>

      {enrichmentStatus?.status === 'not_enriched' && (
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <Sparkles className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            Click "Enrich" to fetch additional company information automatically.
          </p>
        </div>
      )}

      {enrichmentStatus?.status === 'pending' && (
        <div className="bg-yellow-50 rounded-lg p-4 text-center">
          <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2 animate-pulse" />
          <p className="text-sm text-yellow-700">
            Enrichment in progress. This may take a few moments.
          </p>
        </div>
      )}

      {enrichmentStatus?.status === 'failed' && (
        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700 mb-2">
            <XCircle className="w-5 h-5" />
            <span className="font-medium">Enrichment Failed</span>
          </div>
          <p className="text-sm text-red-600">
            {enrichmentStatus.error || 'An error occurred during enrichment.'}
          </p>
        </div>
      )}

      {enrichmentStatus?.status === 'enriched' && enrichedData && (
        <div className="space-y-4">
          {enrichmentStatus.confidence !== undefined && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Confidence:</span>
              <Badge
                variant={
                  enrichmentStatus.confidence >= 0.8
                    ? 'success'
                    : enrichmentStatus.confidence >= 0.5
                    ? 'warning'
                    : 'danger'
                }
                size="sm"
              >
                {LeadEnrichmentService.getConfidenceLabel(enrichmentStatus.confidence)} (
                {Math.round(enrichmentStatus.confidence * 100)}%)
              </Badge>
            </div>
          )}

          {enrichmentStatus.lastEnrichedAt && (
            <p className="text-xs text-gray-500">
              Last enriched: {format(enrichmentStatus.lastEnrichedAt, 'MMM d, yyyy h:mm a')}
              {enrichmentStatus.source && ` via ${enrichmentStatus.source}`}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {enrichedData.businessDescription && (
              <div className="md:col-span-2 bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="w-4 h-4" />
                  Business Description
                </div>
                <p className="text-sm text-gray-600">{enrichedData.businessDescription}</p>
              </div>
            )}

            {enrichedData.foundedYear && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4" />
                  Founded
                </div>
                <p className="text-sm text-gray-900">{enrichedData.foundedYear}</p>
              </div>
            )}

            {enrichedData.annualRevenue && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <DollarSign className="w-4 h-4" />
                  Annual Revenue
                </div>
                <p className="text-sm text-gray-900">{enrichedData.annualRevenue}</p>
              </div>
            )}

            {enrichedData.decisionMakers && enrichedData.decisionMakers.length > 0 && (
              <div className="md:col-span-2 bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Users className="w-4 h-4" />
                  Decision Makers
                </div>
                <div className="space-y-2">
                  {enrichedData.decisionMakers.map((dm, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white rounded p-2 border border-gray-200"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{dm.name}</p>
                        <p className="text-xs text-gray-500">{dm.title}</p>
                      </div>
                      {dm.email && (
                        <a
                          href={`mailto:${dm.email}`}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {enrichedData.additionalEmails && enrichedData.additionalEmails.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4" />
                  Additional Emails
                </div>
                <div className="space-y-1">
                  {enrichedData.additionalEmails.map((email, index) => (
                    <a
                      key={index}
                      href={`mailto:${email}`}
                      className="block text-sm text-blue-600 hover:text-blue-700"
                    >
                      {email}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {enrichedData.socialLinks && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <LinkIcon className="w-4 h-4" />
                  Social Links
                </div>
                <div className="flex flex-wrap gap-2">
                  {enrichedData.socialLinks.linkedin && (
                    <a
                      href={enrichedData.socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      LinkedIn
                    </a>
                  )}
                  {enrichedData.socialLinks.twitter && (
                    <a
                      href={enrichedData.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-1 text-xs bg-sky-100 text-sky-700 rounded hover:bg-sky-200"
                    >
                      Twitter
                    </a>
                  )}
                  {enrichedData.socialLinks.facebook && (
                    <a
                      href={enrichedData.socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                    >
                      Facebook
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {enrichmentStatus.enrichedFields && enrichmentStatus.enrichedFields.length > 0 && (
            <div className="pt-3 border-t">
              <p className="text-xs text-gray-500">
                Enriched fields: {enrichmentStatus.enrichedFields.join(', ')}
              </p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
